import { useAppTranslation } from "@/utils";
import { bnplMerchantApis } from "@payping/api";
import { CuToman, InfoCircleFilled } from "@payping/icons";
import { Button, Flex, Form, Input, Modal, Typography, useMessagedAsyncFn } from "@payping/web-components";
import { FC, useState } from "react";

interface SetCreditModalProps {
  open: boolean;
  onCancel: () => void;
  limitCredit: number;
  onFinish: () => void;
  creditCode: string;
}

interface Inputs {
  limit: number;
}
const BnplManageCreditPageSetCreditModal: FC<SetCreditModalProps> = ({
  onCancel,
  open,
  limitCredit,
  onFinish,
  creditCode,
}) => {
  const { t } = useAppTranslation();

  const [form] = Form.useForm<Inputs>();
  const [showAlert, setShowAlert] = useState(false);

  const limitWatch = Form.useWatch<Inputs>("limit", form);

  const [changeLimitState, changeLimit] = useMessagedAsyncFn(async (walletCreditAmount: number, cc: string) => {
    const res = await bnplMerchantApis.changeWalletCreditAmount({ creditCode: cc, walletCreditAmount });
    return res;
  }, []);

  const handleSubmitForm = async (data: Inputs) => {
    if (!showAlert && limitCredit === data.limit) {
      setShowAlert(true);
      return;
    }

    await changeLimit(data.limit, creditCode);
    onFinish();
  };

  if (showAlert) {
    return (
      <Modal
        footer={null}
        title={null}
        closable={false}
        open={open}
        onCancel={changeLimitState.loading ? undefined : onCancel}
      >
        <Flex gap={"middle"} align="start">
          <InfoCircleFilled width={24} className="tw-text-color-warning" />
          <Flex gap="small" vertical>
            <Typography.Title level={5} className="!tw-m-0">
              {t("shop.bnpl.manageCredit.setLimitModal.warning.title")}
            </Typography.Title>
            <Typography.Paragraph className="!tw-m-0">
              {t("shop.bnpl.manageCredit.setLimitModal.warning.content")}
            </Typography.Paragraph>
            <Flex justify="end" className="tw-pt-4">
              <Button type="primary" onClick={() => form.submit()} loading={changeLimitState.loading}>
                {t("shop.bnpl.manageCredit.setLimitModal.warning.submit")}
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </Modal>
    );
  }
  return (
    <Modal
      footer={null}
      title={t("shop.bnpl.manageCredit.setLimitModal.title")}
      closable
      open={open}
      onCancel={changeLimitState.loading ? undefined : onCancel}
    >
      <Flex vertical gap={"small"}>
        <Typography.Paragraph>{t("shop.bnpl.manageCredit.setLimitModal.content")}</Typography.Paragraph>
        <Form<Inputs> layout="vertical" form={form} onFinish={handleSubmitForm}>
          <Typography.Text className="tw-mb-6 tw-block">
            {t("shop.bnpl.manageCredit.setLimitModal.form.limit.title")}
          </Typography.Text>
          <Form.Item<Inputs>
            name="limit"
            validateTrigger={[""]}
            rules={[
              { required: true },
              {
                validator: (_, value) => {
                  if (parseInt(value, 10) < (limitCredit ?? 1000)) {
                    return Promise.reject(new Error(t("shop.bnpl.manageCredit.setLimitModal.form.limit.minError")));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input.Number
              className={"tw-w-full"}
              step={1000}
              separateThousandth
              min={0}
              suffix={<CuToman width={24} height={24} />}
              placeholder={t("settlement.create.amountInput")}
              onPressEnter={(event) => {
                event.preventDefault();
              }}
              changeOnWheel={false}
              controls={false}
            />
          </Form.Item>
          <Flex justify="end" gap={"small"} className="tw-py-3">
            <Button htmlType="button" onClick={onCancel} disabled={changeLimitState.loading}>
              {t("shop.bnpl.manageCredit.setLimitModal.form.cancel")}
            </Button>
            <Button
              loading={changeLimitState.loading}
              onClick={() => {
                form.submit();
              }}
              htmlType="submit"
              type="primary"
              disabled={!limitWatch}
            >
              {t("shop.bnpl.manageCredit.setLimitModal.form.confirm")}
            </Button>
          </Flex>
        </Form>
      </Flex>
    </Modal>
  );
};

export default BnplManageCreditPageSetCreditModal;
